import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DetailLayout from "@/components/admin/DetailLayout";
import StatusBadge from "@/components/admin/StatusBadge";
import EmptyState from "@/components/admin/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Undo2, FileText } from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

function money(n) { return `$${(n ?? 0).toFixed(2)}`; }

function tone(status) {
  if (status === "paid") return "green";
  if (status === "unpaid") return "amber";
  if (status === "owner_bypass") return "grey";
  if (status === "refunded") return "red";
  return "grey";
}

function refundStatusTone(s) {
  if (s === "succeeded") return "green";
  if (s === "pending") return "amber";
  if (s === "failed") return "red";
  return "grey";
}

function RefundDialog({ open, onClose, onConfirm, busy, amount }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  const canSubmit = !busy && reason.trim().length >= 10;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="dialog-refund" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="dialog-refund-title">Issue full refund</DialogTitle>
          <DialogDescription data-testid="dialog-refund-description">
            This will refund the full {money(amount)} via Stripe. The original payment row is
            preserved for audit; a new entry will appear in the refund history. This action is logged.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <label className="text-xs uppercase tracking-wider text-brand-muted">Reason (required · ≥10 chars)</label>
          <Textarea
            data-testid="dialog-refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer requested refund — duplicate purchase"
            rows={3}
            className="mt-1"
          />
          <div className="text-xs text-brand-muted mt-1">
            {reason.trim().length}/10 characters minimum
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button data-testid="dialog-refund-cancel" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            data-testid="dialog-refund-confirm"
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSubmit}
            variant="destructive"
          >
            {busy ? "Refunding…" : `Refund ${money(amount)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResendReceiptDialog({ open, onClose, onConfirm, busy, email }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="dialog-resend-receipt" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="dialog-resend-receipt-title">Re-send payment receipt</DialogTitle>
          <DialogDescription data-testid="dialog-resend-receipt-description">
            A copy of the original payment receipt will be sent to <span className="font-mono">{email}</span>. The action is logged.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button data-testid="dialog-resend-receipt-cancel" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            data-testid="dialog-resend-receipt-confirm"
            onClick={() => onConfirm()}
            disabled={busy}
          >
            {busy ? "Sending…" : "Send receipt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPaymentDetail({ txId, onBack }) {
  const { user: actor } = useAuth();
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("overview");
  const [err, setErr] = useState("");
  const [dlg, setDlg] = useState(null); // 'refund' | 'resend' | null
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api.get(`/admin/payments/${txId}`)
      .then((r) => { setT(r.data); setErr(""); })
      .catch((e) => setErr(e.message || "Failed to load"));
  }, [txId]);

  useEffect(() => { reload(); }, [reload]);

  const onResend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/payments/${txId}/resend-receipt`);
      toast.success("Receipt resent");
      setDlg(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend");
    } finally { setBusy(false); }
  };

  const onRefund = async (reason) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/payments/${txId}/refund`, { reason });
      toast.success(`Refund ${r.data.status} (${money(r.data.amount_refunded_aud)})`);
      setDlg(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally { setBusy(false); }
  };

  if (err) return <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3">{err}</div>;
  if (!t) return <div className="text-sm text-brand-muted">Loading payment…</div>;

  const isOwnerActor = !!actor?.is_owner;
  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "refunds",  label: "Refund history", count: t.refund_count },
    { key: "receipts", label: "Receipts" },
  ];

  const actions = (
    <>
      <Button
        data-testid="action-resend-receipt"
        variant="outline" size="sm"
        onClick={() => setDlg("resend")}
        disabled={!t.can_resend_receipt}
        title={!t.can_resend_receipt ? "Receipt resend not available for this transaction" : ""}
      >
        <Mail className="w-4 h-4 mr-1" /> Re-send receipt
      </Button>
      {isOwnerActor && (
        <Button
          data-testid="action-refund"
          variant="outline" size="sm"
          onClick={() => setDlg("refund")}
          disabled={!t.can_refund}
          title={!t.can_refund ? (t.is_owner_bypass ? "Owner-bypass transactions cannot be refunded" : "This transaction is not refundable") : ""}
        >
          <Undo2 className="w-4 h-4 mr-1" /> Issue refund
        </Button>
      )}
    </>
  );

  let banner = null;
  if (t.refund_status === "refunded") {
    banner = (
      <div className="text-xs px-3 py-2 bg-red-50 border border-red-200 text-red-700" data-testid="payment-banner-refunded">
        Refunded — {money(t.refunded_total_aud)} returned to customer.
      </div>
    );
  } else if (t.is_owner_bypass) {
    banner = (
      <div className="text-xs px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700" data-testid="payment-banner-bypass">
        Owner-bypass transaction — no Stripe charge was made. Refunds and receipts are not available.
      </div>
    );
  }

  return (
    <>
      <DetailLayout
        testId="admin-payment-detail"
        onBack={onBack}
        backLabel="← Back to payments"
        title={`${money(t.amount_aud)} ${t.currency || "AUD"}`}
        status={<StatusBadge tone={tone(t.status)} label={t.status} testId="payment-status" />}
        subText={(
          <>
            Customer ·&nbsp;
            <span className="font-mono text-xs">{t.customer?.email || "—"}</span>
            &nbsp;· Project ·&nbsp;
            <span className="text-xs">{t.project?.title || "—"}</span>
            &nbsp;· created {relTime(t.created_at)}
          </>
        )}
        actions={actions}
        banner={banner}
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === "overview" && (
          <div data-testid="payment-tab-overview" className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-brand-linen p-4">
              <div className="overline mb-3">Transaction</div>
              <Row label="Amount" value={`${money(t.amount_aud)} ${t.currency || "AUD"}`} />
              <Row label="Status" value={<StatusBadge tone={tone(t.status)} label={t.status} />} />
              <Row label="Stripe session id" mono value={t.session_id} />
              <Row label="Stripe payment intent" mono value={t.stripe_payment_intent_id || "—"} />
              <Row label="Refunded total" value={money(t.refunded_total_aud)} />
              <Row label="Refund attempts" value={t.refund_count} />
              <Row label="Created" value={t.created_at ? new Date(t.created_at).toLocaleString("en-AU") : "—"} />
              <Row label="Updated" value={relTime(t.updated_at)} />
            </div>
            <div className="bg-white border border-brand-linen p-4">
              <div className="overline mb-3">Customer & project</div>
              <Row label="Customer" value={t.customer?.name} />
              <Row label="Email" mono value={t.customer?.email} />
              <Row label="Project" value={t.project?.title} />
            </div>
          </div>
        )}

        {tab === "refunds" && (
          <div data-testid="payment-tab-refunds">
            {(t.refund_history || []).length === 0 ? (
              <EmptyState icon={Undo2} title="No refund attempts" caption="No refunds have been issued for this payment." testId="payment-refunds-empty" />
            ) : (
              <div className="bg-white border border-brand-linen">
                {t.refund_history.map((r) => (
                  <div key={r.id} className="px-4 py-3 border-b border-brand-linen/40 last:border-0 flex items-start gap-4" data-testid={`refund-row-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-brand-ink">{money(r.amount_aud)} · {r.reason}</div>
                      <div className="text-xs text-brand-muted mt-1">
                        by {r.actor_email} · {relTime(r.created_at)}
                        {r.stripe_refund_id ? <> · <span className="font-mono">{r.stripe_refund_id.slice(0, 24)}</span></> : null}
                      </div>
                      {r.stripe_error && (
                        <div className="text-xs text-destructive mt-1">{r.stripe_error}</div>
                      )}
                    </div>
                    <StatusBadge tone={refundStatusTone(r.status)} label={r.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "receipts" && (
          <div data-testid="payment-tab-receipts">
            {(t.receipt_history || []).length === 0 ? (
              <EmptyState icon={FileText} title="No receipts" caption="No receipt events yet for this payment." testId="payment-receipts-empty" />
            ) : (
              <div className="bg-white border border-brand-linen">
                {t.receipt_history.map((r) => (
                  <div key={r.id} className="px-4 py-3 border-b border-brand-linen/40 last:border-0 flex items-baseline justify-between gap-4" data-testid={`receipt-row-${r.id}`}>
                    <div className="text-sm text-brand-ink">{r.summary}</div>
                    <div className="text-xs text-brand-muted">{r.kind === "payment.paid" ? "initial" : "resend"} · {relTime(r.ts)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DetailLayout>

      <ResendReceiptDialog
        open={dlg === "resend"}
        onClose={() => setDlg(null)}
        onConfirm={onResend}
        busy={busy}
        email={t.customer?.email}
      />
      <RefundDialog
        open={dlg === "refund"}
        onClose={() => setDlg(null)}
        onConfirm={onRefund}
        busy={busy}
        amount={t.amount_aud}
      />
    </>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-brand-linen/40 last:border-0">
      <div className="text-xs uppercase tracking-wider text-brand-muted">{label}</div>
      <div className={`text-sm text-brand-ink text-right ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}
